import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { trainingsplanerAPI } from '@/lib/api';

/* Trainingsplaner › Zyklus aufklappen: Plan umbenennen (10.10.2026). Eigene Pläne für PT-Kunden
   heißen meist nach dem Kunden. Wird die Person gelöscht, nennt die Lösch-Bestätigung unter
   Einstellungen › Datenschutz diese Pläne — hier bekommen sie einen neuen Namen. Geht als einzige
   Änderung auch bei archivierten Plänen; der Server zieht den Namen in den Kraftlog-Einträgen nach. */

const MIN = 2;
const MAX = 120;   // wie beim Anlegen (ZyklusIn.name)

export default function PlanName({ z, onGeaendert }) {
  const { isAdminPermissions, userPermissions } = useAuth();
  // Wie „Für wen ist dieser Plan?“ — der Server prüft dasselbe (Ersteller, Headcoach, Leitung)
  const darf = isAdminPermissions || !!(userPermissions || {})['trainingsplaner.edit'];
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(z.name);
  const [busy, setBusy] = useState(false);
  if (!darf) return null;

  const sauber = name.trim();
  const gueltig = sauber.length >= MIN && sauber.length <= MAX;
  const auf = () => { setName(z.name); setOffen(true); };

  const speichern = async () => {
    if (!gueltig || busy) return;
    setBusy(true);
    try {
      await trainingsplanerAPI.name(z.id, z.version, sauber);
      toast.success('Name gespeichert');
      setOffen(false);
    } catch (e) {
      // Das Feld bleibt offen: Nach dem Neuladen stimmt die Version, ein zweiter Klick genügt
      const d = e?.response?.data?.detail;
      toast.error(e?.response?.status === 409 ? 'Der Zyklus wurde inzwischen geändert – die Liste ist neu geladen, bitte noch einmal.' : (typeof d === 'string' ? d : 'Nicht gespeichert'));
    }
    setBusy(false);
    onGeaendert?.();
  };

  const kopf = <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name des Plans</p>;
  if (!offen) {
    return (
      <div className="space-y-1" data-testid={`plan-name-${z.id}`}>
        {kopf}
        <p className="text-sm flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium break-words">{z.name}</span>
          <button type="button" onClick={auf} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`plan-name-aendern-${z.id}`}>
            <Pencil className="w-3 h-3" /> Umbenennen
          </button>
        </p>
        {z.status === 'archiviert' && <p className="text-xs text-muted-foreground">Dieser Plan ist archiviert. Nur der Name lässt sich noch ändern.</p>}
      </div>
    );
  }
  return (
    <form className="space-y-1" onSubmit={(e) => { e.preventDefault(); speichern(); }} data-testid={`plan-name-${z.id}`}>
      {kopf}
      <div className="flex flex-wrap items-center gap-2">
        {/* readOnly statt disabled: Ein gesperrtes Feld verlöre den Fokus — Enter nach „bitte noch einmal“ liefe ins Leere */}
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={MAX} autoFocus readOnly={busy}
          onKeyDown={(e) => { if (e.key === 'Escape') setOffen(false); }}
          aria-label="Neuer Name des Plans" className="h-8 max-w-sm" data-testid={`plan-name-feld-${z.id}`} />
        <Button type="submit" size="sm" disabled={!gueltig || busy} data-testid={`plan-name-speichern-${z.id}`}>Speichern</Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setOffen(false)}>Abbrechen</Button>
      </div>
      {!gueltig && <p className="text-xs text-amber-700 dark:text-amber-400">Der Name braucht {MIN} bis {MAX} Zeichen.</p>}
      <p className="text-xs text-muted-foreground">Der neue Name gilt überall: in der Trainer-App, in der App der Mitglieder und in den Kraftlog-Einträgen zu diesem Plan.</p>
    </form>
  );
}
